import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'

import { DepositCancelReason } from '../core/response.types'
import { createReason, patchReason } from '../core/api'
import { CODE_PATTERN } from '../core/consts'
import { getErrorText } from '../../../../../common/api/errors'

interface Props {
  show: boolean
  onClose: () => void
  editing?: DepositCancelReason | null
  onSaved: () => void
}

interface FormState {
  code: string
  title: string
  enabled: boolean
}

const empty: FormState = { code: '', title: '', enabled: true }

export const ReasonModal: React.FC<Props> = ({ show, onClose, editing, onSaved }) => {
  const isEdit = !!editing
  const [form, setForm] = useState<FormState>(empty)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!show) return
    setError(null)
    setForm(
      editing ? { code: editing.code, title: editing.title, enabled: editing.enabled } : empty
    )
  }, [show, editing])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    // The code is looked up as a translation key on the payment page, so it is checked here
    // before the request rather than left to a server-side field error.
    if (!CODE_PATTERN.test(form.code)) {
      setError('Ключ: латиница в нижнем регистре, цифры и «_», до 64 символов')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const body = { code: form.code, title: form.title, enabled: form.enabled }
      const r = isEdit && editing ? await patchReason(editing.id, body) : await createReason(body)
      if (!r.data.success) {
        setError(getErrorText(r, SAVE_FALLBACK, { labels: ERROR_MESSAGE }))
        return
      }
      onSaved()
      onClose()
    } catch (e: unknown) {
      // The interceptor rejects with the response itself and this URL is out of its alerts: this is the only message.
      setError(getErrorText(e, SAVE_FALLBACK, { labels: ERROR_MESSAGE }))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? 'Редактирование причины отмены' : 'Новая причина отмены'}
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className='mb-3'>
            <label className='form-label required' htmlFor='reasonCode'>
              Ключ
            </label>
            <input
              id='reasonCode'
              type='text'
              className='form-control'
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              required
              maxLength={64}
              placeholder='no_such_bank'
            />
            <div className='text-muted small'>
              Ключ фразы на платёжной странице. Новый ключ нужно добавить в переводы страницы — без
              перевода клиент пункт не увидит.
            </div>
          </div>
          <div className='mb-3'>
            <label className='form-label required' htmlFor='reasonTitle'>
              Подпись
            </label>
            <input
              id='reasonTitle'
              type='text'
              className='form-control'
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              maxLength={255}
            />
            <div className='text-muted small'>Видят только сотрудники.</div>
          </div>
          <div className='form-check mb-3'>
            <input
              id='reasonEnabled'
              type='checkbox'
              className='form-check-input'
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            <label className='form-check-label' htmlFor='reasonEnabled'>
              Показывать клиенту
            </label>
          </div>
          {error && <div className='alert alert-danger py-2'>{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type='button' className='btn btn-light' onClick={onClose} disabled={saving}>
            Отмена
          </button>
          <button type='submit' className='btn btn-primary' disabled={saving}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

const ERROR_MESSAGE: Record<string, string> = {
  DEPOSIT_CANCEL_REASON_EXISTS: 'Причина с таким ключом уже существует',
  DEPOSIT_CANCEL_REASON_NOT_FOUND: 'Причина не найдена — обновите страницу',
}

const SAVE_FALLBACK = 'Не удалось сохранить причину'

export default ReasonModal
